import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {AnswerOptionExecution} from '../../../data/models/execution/answer-execution';
import {AnswerExecutionService} from '../../../services/api/execution/answer-execution.service';
import {MatRadioChange} from '@angular/material/radio';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {AnswerExecution} from '../answer-execution';

@Component({
  selector: 'app-answer-options-execution',
  templateUrl: './answer-options-execution.component.html',
  styleUrls: ['./answer-options-execution.component.css']
})
export class AnswerOptionsExecutionComponent implements OnInit, OnChanges, OnDestroy, AnswerExecution {

  @Input() executionId!: number;
  @Input() questionId!: number;
  @Input() options!: AnswerOptionExecution[];
  @Input() multiple = false;
  @Input() minAnswers?: number | null;
  @Input() maxAnswers?: number | null;
  @Input() required = true;

  @Output() optionsChange = new EventEmitter<AnswerOptionExecution[]>();

  selectionHint: string | null = null;
  validationMessage: string | null = null;
  private validationMessageTimeout?: any;

  constructor(
    private answerExecutionService: AnswerExecutionService,
  ) {
  }

  ngOnInit(): void {
    this.updateSelectionHint();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.minAnswers || changes.maxAnswers || changes.multiple || changes.required) {
      this.updateSelectionHint();
    }
    if (changes.options) {
      if (this.options) {
        this.options = this.options.map(option => this.normalizeOption(option));
      }
      this.clearValidationMessage();
    }
    if (changes.multiple && !this.multiple) {
      this.clearValidationMessage();
    }
  }

  ngOnDestroy(): void {
    this.clearValidationMessage();
  }

  onOptionRadioChange(option: AnswerOptionExecution, event: MatRadioChange): void {
    if (option.optionId === +event.value) {
      const previousOptionsState = this.cloneOptions();
      this.applyOptionUpdate({
        ...option,
        selected: true,
      });
      this.answerExecutionService.selectAnswerOption(this.executionId, this.questionId, option.optionId)
        .then(o1 => {
          this.applyOptionUpdate(o1);
          this.clearValidationMessage();
          this.optionsChange.emit(this.options);
        })
        .catch(() => {
          if (previousOptionsState) {
            this.options = previousOptionsState;
          }
        });
    }
  }

  onOptionRadioClick(option: AnswerOptionExecution, event: MouseEvent): void {
    if (!this.isOptionSelected(option)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const previousOptionsState = this.cloneOptions();
    this.applyOptionUpdate({
      ...option,
      selected: false,
    });

    this.answerExecutionService.setAnswerOptionSelected(
      this.executionId,
      this.questionId,
      option.optionId,
      false,
    )
      .then(o1 => {
        this.applyOptionUpdate(o1);
        this.optionsChange.emit(this.options);
      })
      .catch(() => {
        if (previousOptionsState) {
          this.options = previousOptionsState;
        }
      });
  }

  onOptionCheckChange(option: AnswerOptionExecution, event: MatCheckboxChange): void {
    const isSelected = this.normalizeSelected(option.selected);
    if (isSelected !== event.checked) {
      const selectedCount = this.getSelectedCount();
      const minRequired = this.getMinRequired();

      if (!event.checked && minRequired > 0) {
        const newCount = selectedCount - 1;
        if (newCount < minRequired) {
          this.clearValidationMessage();
        }
      }

      if (event.checked && this.isOptionBlocked(option)) {
        event.source.checked = this.normalizeSelected(option.selected);
        return;
      }

      const blockedSelectedCount = this.countSelectedBlockedBy(option);

      if (event.checked && this.maxAnswers != null) {
        const newCount = selectedCount - blockedSelectedCount + 1;
        if (newCount > this.maxAnswers) {
          event.source.checked = this.normalizeSelected(option.selected);
          this.showValidationMessage(`Можно выбрать максимум ${this.formatVariantCount(this.maxAnswers)}.`);
          return;
        }
      }

      const previousOptionsState = this.cloneOptions();
      this.applyOptionUpdate({
        ...option,
        selected: event.checked,
      });
      this.answerExecutionService.setAnswerOptionSelected(this.executionId, this.questionId, option.optionId, event.checked)
        .then(o1 => {
          this.applyOptionUpdate(o1);
          const selectedAfterUpdate = this.getSelectedCount();
          if (this.isSelectionWithinLimits()) {
            this.clearValidationMessage();
          } else if (this.multiple && this.maxAnswers != null && selectedAfterUpdate > this.maxAnswers) {
            this.showValidationMessage(`Можно выбрать максимум ${this.formatVariantCount(this.maxAnswers)}.`);
          } else {
            this.clearValidationMessage();
          }
          this.optionsChange.emit(this.options);
        })
        .catch(() => {
          if (previousOptionsState) {
            this.options = previousOptionsState;
          }
          const revertedOption = previousOptionsState?.find(o => o.optionId === option.optionId);
          event.source.checked = this.normalizeSelected(revertedOption ? revertedOption.selected : option.selected);
        });
    }
  }

  submitAnswer(): Promise<void> {
    const selectedCount = this.getSelectedCount();

    if (!this.required && selectedCount === 0) {
      this.clearValidationMessage();
      return Promise.resolve();
    }

    if (!this.isSelectionWithinLimits()) {
      const minRequired = this.getMinRequired();

      if (selectedCount < minRequired) {
        this.clearValidationMessage();
      } else if (this.multiple && this.maxAnswers != null && selectedCount > this.maxAnswers) {
        this.showValidationMessage(`Можно выбрать максимум ${this.formatVariantCount(this.maxAnswers)}.`);
      }

      return Promise.reject(new Error('answer-options-validation-error'));
    }

    this.clearValidationMessage();
    return Promise.resolve();
  }

  isCheckboxDisabled(option: AnswerOptionExecution): boolean {
    if (!this.multiple) {
      return false;
    }

    if (this.isOptionBlocked(option) && !this.normalizeSelected(option.selected)) {
      return true;
    }

    const selectedCount = this.getSelectedCount();

    if (!this.normalizeSelected(option.selected) && this.maxAnswers != null) {
      const blockedSelectedCount = this.countSelectedBlockedBy(option);
      const newCount = selectedCount - blockedSelectedCount + 1;
      if (newCount > this.maxAnswers) {
        return true;
      }
    }

    return false;
  }

  private updateSelectionHint(): void {
    if (!this.multiple) {
      this.selectionHint = null;
      return;
    }

    const min = this.minAnswers;
    const max = this.maxAnswers;

    const hints: string[] = [];

    if (min == null && max == null) {
      const minRequired = this.getMinRequired();
      if (minRequired > 0) {
        hints.push(this.required ? `Минимум вариантов: ${minRequired}.` : `Минимум вариантов при ответе: ${minRequired}.`);
      }
    } else if (min != null && max != null) {
      hints.push(`Количество вариантов: от ${min} до ${max}.`);
    } else if (min != null) {
      hints.push(this.required ? `Минимум вариантов: ${min}.` : `Минимум вариантов при ответе: ${min}.`);
    } else {
      hints.push(`Максимум вариантов: ${max}.`);
    }

    if (!this.required) {
      hints.push('Вопрос можно пропустить.');
    }

    this.selectionHint = hints.length > 0 ? hints.join(' ') : null;
  }

  private getSelectedOptionIds(): Set<number> {
    const selectedIds = this.options?.filter(option => this.normalizeSelected(option.selected)).map(option => option.optionId) || [];
    return new Set(selectedIds);
  }

  private getSelectedCount(): number {
    return this.getSelectedOptionIds().size;
  }

  isOptionBlocked(option: AnswerOptionExecution): boolean {
    if (!option.blockingOptionIds || option.blockingOptionIds.length === 0) {
      return false;
    }
    const selectedIds = this.getSelectedOptionIds();
    return option.blockingOptionIds.some(id => selectedIds.has(id));
  }

  getBlockingHint(option: AnswerOptionExecution): string | null {
    const blockingOptionNames = this.getBlockingOptionNames(option);
    if (blockingOptionNames.length === 0) {
      return null;
    }
    return `Недоступно из-за конфликтов с: ${blockingOptionNames.map(name => `«${name}»`).join(', ')}`;
  }

  private countSelectedBlockedBy(option: AnswerOptionExecution): number {
    if (!option.blockedOptionIds || option.blockedOptionIds.length === 0) {
      return 0;
    }
    const selectedIds = this.getSelectedOptionIds();
    return option.blockedOptionIds.filter(id => selectedIds.has(id)).length;
  }

  private getMinRequired(): number {
    if (!this.multiple) {
      return 1;
    }

    return this.minAnswers != null ? this.minAnswers : 1;
  }

  private isSelectionWithinLimits(): boolean {
    const selectedCount = this.getSelectedCount();

    if (selectedCount === 0) {
      return !this.required;
    }

    if (selectedCount < this.getMinRequired()) {
      return false;
    }

    if (this.multiple) {
      if (this.maxAnswers != null && selectedCount > this.maxAnswers) {
        return false;
      }
    } else if (selectedCount > 1) {
      return false;
    }

    return true;
  }

  private formatVariantCount(count: number): string {
    const absCount = Math.abs(count);
    const mod10 = absCount % 10;
    const mod100 = absCount % 100;

    if (mod10 === 1 && mod100 !== 11) {
      return `${count} вариант`;
    }

    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
      return `${count} варианта`;
    }

    return `${count} вариантов`;
  }

  private getBlockingOptionNames(option: AnswerOptionExecution): string[] {
    if (!option.blockingOptionIds || option.blockingOptionIds.length === 0 || !this.options) {
      return [];
    }
    const selectedIds = this.getSelectedOptionIds();
    const blockingIds = option.blockingOptionIds.filter(id => selectedIds.has(id));
    if (blockingIds.length === 0) {
      return [];
    }
    return this.options
      .filter(o => blockingIds.includes(o.optionId))
      .map(o => o.text);
  }

  private showValidationMessage(message: string): void {
    this.validationMessage = message;
    if (this.validationMessageTimeout) {
      clearTimeout(this.validationMessageTimeout);
    }
    this.validationMessageTimeout = setTimeout(() => {
      this.validationMessage = null;
      this.validationMessageTimeout = undefined;
    }, 4000);
  }

  private clearValidationMessage(): void {
    if (this.validationMessageTimeout) {
      clearTimeout(this.validationMessageTimeout);
      this.validationMessageTimeout = undefined;
    }
    this.validationMessage = null;
  }

  private applyOptionUpdate(updatedOption: AnswerOptionExecution): void {
    const normalizedOption = this.normalizeOption(updatedOption);
    const blockedIds = new Set(normalizedOption.blockedOptionIds || []);
    const isSelected = !!normalizedOption.selected;
    const currentOptions = this.options || [];

    this.options = currentOptions.map(option => {
      if (option.optionId === normalizedOption.optionId) {
        return this.normalizeOption({
          ...option,
          ...normalizedOption,
        });
      }

      const shouldUnselect = isSelected && (
        blockedIds.has(option.optionId) ||
        (!this.multiple && this.normalizeSelected(option.selected))
      );

      if (shouldUnselect) {
        return this.normalizeOption({
          ...option,
          selected: false,
        });
      }

      return this.normalizeOption(option);
    });
  }

  private cloneOptions(): AnswerOptionExecution[] | undefined {
    if (!this.options) {
      return undefined;
    }
    return this.options.map(option => this.normalizeOption(option));
  }

  private normalizeOption(option: AnswerOptionExecution): AnswerOptionExecution {
    return {
      ...option,
      selected: this.normalizeSelected(option.selected),
      blockedOptionIds: option.blockedOptionIds ? [...option.blockedOptionIds] : [],
      blockingOptionIds: option.blockingOptionIds ? [...option.blockingOptionIds] : [],
    };
  }

  isOptionSelected(option: AnswerOptionExecution): boolean {
    return this.normalizeSelected(option.selected);
  }

  private normalizeSelected(selected: AnswerOptionExecution['selected']): boolean {
    if (typeof selected === 'string') {
      return selected.toLowerCase() === 'true';
    }
    return !!selected;
  }

}
